import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import {
	ANIMATION_NAMES,
	COLLIDABLE_MESH_NAME,
	DEFAULT_FPS_CAMERA_Y_OFFSET,
	DEFAULT_FPS_MIN_DISTANCE_FROM_CAMERA,
	DEFAULT_FPS_MAX_DISTANCE_FROM_CAMERA,
	DEFAULT_GRAVITY,
	DEFAULT_WALK_VELOCITY,
	DIRECTIONS,
	MAXIMUM_MULTIPLIER,
	VISUALIZE_COLLIDERS,
	AVATAR_COLLIDER_THRESHOLD_MIN,
	AVATAR_COLLIDER_THRESHOLD_MAX,
} from '../Avatars/AvatarControls/Constants';
import {
	FORWARD_BACKWARD_KEYS,
	DEFAULT_CAMERA_ROTATION_SPEED,
	DEFAULT_JOYSTICK_ROTATION_SENSITIVITY,
} from './constants';
import {
	acceleratedRaycast,
	MeshBVH,
	MeshBVHHelper,
	StaticGeometryGenerator,
} from 'three-mesh-bvh';

import {
	boxBoundSkinnedMeshGroup,
	createCubeFromBox3,
} from '../Avatars/AvatarControls/ColliderHelpers';

import { SphereGeometry } from 'three';
import {
	getDistanceXInCameraDirection,
	rotateGLBTowardsCamera,
} from '@3jss/utils/cameraHelpers';
import { CONTROL_MODES } from '@3jss/utils/constants';
import HotspotsFadeInOutEffect from '../Hotspot/HotspotsFadeInOutEffect';

/**
 * @class RT3DControls
 * Controls interface for Real time 3D Scenes
 *
 * - Handles movement and rotation of avatar
 * - Handles collision detection and response
 * - Handles camera movement and collision
 * - Sets up Keyboard listeners
 * - Sets up Animation Listeners
 *
 * **/
class RT3DControls {
	#animationKeysMap = {};
	#isWalking = false;
	#ready = false;
	#moveAngle = 0;
	yOffset = null;
	/**
	 * @constructor
	 * @param {THREE.WebGLRenderer} renderrer - WebGLRenderer instance
	 * @param {THREE.Scene} scene - Scene instance
	 * @param {THREE.PerspectiveCamera} camera - Camera instance
	 * @param {CustomCameraControls} orbitControl - OrbitControls instance
	 * @param {THREE.Object3D} storeObj - Store 3D model object
	 * @param {int|float} [walkVelocity=6] - Walk velocity for controls
	 * @param {Object} orbitControlConfig - Custom configuration to be applied to orbit controls
	 * @param controlMode
	 * @param sceneModelConfig
	 * @param {Number} orbitControlConfig.min_distance - Minimum distance from the camera to the target
	 * @param {Number} orbitControlConfig.max_distance - Maximum distance from the camera to the target
	 */
	constructor(
		orbitControl,
		camera,
		scene,
		storeObj,
		walkVelocity = DEFAULT_WALK_VELOCITY,
		orbitControlConfig = {},
		renderrer,
		controlMode = CONTROL_MODES.AVATAR_NAVIGATION,
		sceneModelConfig,
		rotateCameraTowardsMovementDirection
	) {
		this.enabled = true;
		this.scene = scene;
		this.camera = camera;
		this.renderrer = renderrer;
		this.walkVelocity = walkVelocity;
		this.originalWalkVelocity = walkVelocity;
		this.orbitControl = orbitControl;
		this.rotateCameraTowardsMovementDirection = rotateCameraTowardsMovementDirection;

		this.sceneModelConfig = sceneModelConfig;

		this.controlMode = controlMode;
		this.objectToTrackConnected = false;

		this.colliderBoundingBox = new THREE.Box3();
		this.invertedWorldMatrix = new THREE.Matrix4();
		this.capsuleSegment = new THREE.Line3();
		this.triPoint = new THREE.Vector3();
		this.capsulePoint = new THREE.Vector3();
		this.newColliderPosition = new THREE.Vector3();
		this.colliderDeltaPosition = new THREE.Vector3();

		this.walkDirection = new THREE.Vector3();
		this.rotationAxis = new THREE.Vector3(0, 1, 0);
		this.rotateQuarternion = new THREE.Quaternion();
		this.cameraTarget = new THREE.Vector3();
		this.storeObj = storeObj;

		this.joyStickFiring = false;
		this.hotspotsFadeInOutEffect = new HotspotsFadeInOutEffect();

		document.addEventListener(
			'visibilitychange',
			this._handleVisibilityChange,
		);

		this._loadColliderEnvironment(this.getCollidableMeshes(storeObj));

		this.playerVelocity = new THREE.Vector3(0, 0, 0);

		this.updateOrbitControlConfig(orbitControlConfig);
		this._addKeyListeners();

		this.clock = new THREE.Clock();

		this.addToAnimationLoop();
		this.#ready = true;
		this.isWalking = false;

		this.interactionRaycaster = new THREE.Raycaster();
		this.interactionRaycaster.near = 0.1;
		this.interactionRaycaster.far = 3;

		// Pointer to track active hotspot and prevent subsequent clicks/actions on it
		this._activeHotspotId = null;
	}

	updateSceneCollider = () => {
		// Refresh collider environment
		this._loadColliderEnvironment(this.getCollidableMeshes(this.storeObj));
	};

	resetAnimationsKeyMap = () => {
		this.#animationKeysMap = {};
	};

	setupAnimationKeysMap = (animations) => {
		if (!animations) return;
		this.resetAnimationsKeyMap();
		animations.forEach((animation) => {
			let animKey =
				animation.key === 'space' ? ' ' : animation.key.toLowerCase();
			this.#animationKeysMap[animKey] = animation.name;
		});
	};

	/**
	 * @method
	 * @desc
	 * Connect a target to be controlled (moved) by controls. This object is used to move the camera around the space.
	 * @param {THREE.Object3D} objectToTrack - A Object3D to track
	 * @param isAvatarChange
	 *
	 * TODO: Check avatar disconnect logic
	 */
	connectTargetToControls = (objectToTrack, isAvatarChange = false) => {
		this.objectToTrack = objectToTrack;
		this.objectToTrackConnected = true;
		this._setupPlayerCollider(isAvatarChange);
	};

	/**
	 * @method
	 * @desc
	 * Additional step when connecting an avatar to controls.
	 * @param {Avatar} avatar - Instance of {@link Avatar}
	 * @param {Object} config - Configuration object for the avatar
	 * @param {Array} config.animations - List of animations for the avatar
	 * @param isAvatarChange
	 */
	connectAvatarToControls = (avatar, config, isAvatarChange = false) => {
		const { animations } = config;

		this.avatar = avatar;
		this.setupAnimationKeysMap(animations);

		if (isAvatarChange) {
			// if its an avatar switch, copy the old position, dont recalculate landing position
			// also keep track of this.avatarChanged because sometimes _setupPlayerCollider gets called twice
			let pos = this.playerCollider.position.clone();
			pos.y += 0.005;
			this.avatar.model.position.copy(pos);
		} else {
			// Avatar initial load

			// Find distance at which to render the avatar so that it will be completely displayed.
			// 1. Find the size of the avatar using bounding box computed in Avatar.js
			this.playerBoxSize = new THREE.Vector3();
			this.avatar.model.bbox.getSize(this.playerBoxSize);

			// 2. Use getDistanceToFitBox to get distance
			this._avatarDistanceFromCamera =
				this.orbitControl.getDistanceToFitBox(
					this.playerBoxSize.x,
					this.playerBoxSize.y,
					this.playerBoxSize.z,
				);

			// 3. Get a vector `distance` units in direction the camera is looking.
			// TODO: Make the offset dynamic
			const landingPosition = getDistanceXInCameraDirection(
				this.camera,
				this._avatarDistanceFromCamera,
				this.sceneModelConfig?.position.y + 0.1,
			);

			this.avatar.model.position.copy(landingPosition);
		}

		// 4. Rotate the avatar towards the camera
		rotateGLBTowardsCamera(avatar.model, this.camera);

		// 5. Set the min and max distance for the orbit controls
		// TODO: Make the offset dynamic
		this.orbitControl.minDistance = this._avatarDistanceFromCamera * 0.5;
		this.orbitControl.maxDistance = this._avatarDistanceFromCamera * 1.5;

		this.connectTargetToControls(avatar.model, isAvatarChange);
		this._updateCameraTarget();

		this._resetKeys();
	};

	/**
	 * @method
	 * @desc
	 * Disconnect the AVATAR from controls. Reverses everything done in {@link RT3DControls#connectAvatarToControls}
	 */
	disconnectAvatarFromControls = () => {
		this.setOverrideOrbitControlsConfig({});
		this.objectToTrackConnected = false;
		this.avatar = null;
		this.resetAnimationsKeyMap();
		this._resetKeys();
	};

	/**
	 * @method
	 * @desc
	 * Set walking speed for controls
	 * @param {Number} walkVelocity - A number to set as walking velocity
	 */
	setWalkVelocity = (walkVelocity) => {
		this.walkVelocity = walkVelocity;
	};

	/**
	 * @method
	 * @param {Object} config
	 * @param {Number} config.min_distance
	 */
	setOverrideOrbitControlsConfig = (config) => {
		this._overrideOrbitControlsConfig = config;
	};

	_getTempColliderSize = () => {
		return new THREE.Vector3(1, 1, 1);
	};

	// Setup Functions
	_setupPlayerCollider(isAvatarChange = false) {
		if (!this.objectToTrackConnected) return;

		const boundingBox = this.avatar
			? boxBoundSkinnedMeshGroup(this.objectToTrack)
			: new THREE.Box3().setFromObject(this.objectToTrack);

		if (VISUALIZE_COLLIDERS) {
			this.cube = createCubeFromBox3(boundingBox, 'red');
			this.scene.add(this.cube);
		}

		this.boundingBoxSize = new THREE.Vector3();
		boundingBox.getSize(this.boundingBoxSize);

		//without this check, the colliders are set to be very huge (spanning the whole scene), but after the avatar is loaded and it's animation starts, it's size become reasonable.
		// the final avatar size is around 1 unit
		// this all can be solved by adding a flag that signals whether the Avatar is loaded and ready
		if (
			this.boundingBoxSize.x > AVATAR_COLLIDER_THRESHOLD_MAX ||
			this.boundingBoxSize.y > AVATAR_COLLIDER_THRESHOLD_MAX ||
			this.boundingBoxSize.z > AVATAR_COLLIDER_THRESHOLD_MAX ||
			this.boundingBoxSize.x < AVATAR_COLLIDER_THRESHOLD_MIN ||
			this.boundingBoxSize.y < AVATAR_COLLIDER_THRESHOLD_MIN ||
			this.boundingBoxSize.z < AVATAR_COLLIDER_THRESHOLD_MIN
		) {
			this.boundingBoxSize = this._getTempColliderSize();
			this.usingTempCollider = true;
		} else {
			this.usingTempCollider = false;
		}

		this.playerColliderRadius = this.boundingBoxSize.x / 2;
		this.playerCollider = new THREE.Mesh(
			new RoundedBoxGeometry(
				this.boundingBoxSize.x,
				this.boundingBoxSize.y,
				this.boundingBoxSize.z,
				1,
				this.boundingBoxSize.x / 2,
			),
			new THREE.MeshStandardMaterial({
				wireframe: true,
			}),
		);

		this.playerCollider.geometry.translate(
			0,
			this.boundingBoxSize.y / 2 - this.playerColliderRadius,
			0,
		);
		this.playerCollider.capsuleInfo = {
			radius: this.playerColliderRadius,
			segment: new THREE.Line3(
				new THREE.Vector3(),
				new THREE.Vector3(0, this.boundingBoxSize.y, 0.0),
			),
		};

		this.playerCollider.visible = VISUALIZE_COLLIDERS;
		// this.scene.add(this.playerCollider);

		// Setup camera sphere used for collision detection
		this.playerCameraBoundingSphere = new THREE.Mesh(
			new SphereGeometry(this.boundingBoxSize.y * 2),
			new THREE.MeshBasicMaterial({
				wireframe: true,
			}),
		);

		this.playerCameraBoundingSphere.geometry.translate(
			0,
			this.boundingBoxSize.y / 2,
			0,
		);

		this.playerCollider.position.copy(this.objectToTrack.position);
		this.playerCameraBoundingSphere.position.copy(
			this.objectToTrack.position,
		);
		this.playerColliderReady = true;
	}

	/**
	 * Prepares BVH collider using the list of meshes provided in arguments
	 *
	 * @param {Array} meshes - An array of meshes to include in BVH
	 * @private
	 */
	_loadColliderEnvironment = (meshes) => {
		let visibilityMap;

		//meshes must be visible initially to set up BVH
		visibilityMap = meshes.map((i) => i.visible);

		// Show all the meshes to generate BVH
		meshes.forEach((i) => (i.visible = true));

		// Generate BVH
		const staticGenerator = new StaticGeometryGenerator(meshes);
		staticGenerator.attributes = ['position'];
		const mergedGeometry = staticGenerator.generate();
		mergedGeometry.boundsTree = new MeshBVH(mergedGeometry, {
			lazyGeneration: false,
		});

		// Create one collider mesh for all the meshes
		this.storeCollider = new THREE.Mesh(mergedGeometry);

		// Add accelerated raycast to storeCollider mesh
		this.storeCollider.raycast = acceleratedRaycast;

		// Set collider mesh for camera control collision detection
		this.orbitControl.colliderMeshes = [this.storeCollider];

		//the store is static, only need to invert once instead of every frame
		this.invertedWorldMatrix.copy(this.storeCollider.matrixWorld).invert();

		//hide back what was initially hidden
		meshes.forEach((mesh, idx) => (mesh.visible = visibilityMap[idx]));

		if (VISUALIZE_COLLIDERS) {
			let visualizer = new MeshBVHHelper(this.storeCollider);
			this.scene.add(visualizer);
		}
	};

	/**
	 * Updates the orbit control configuration
	 *
	 * @desc
	 * This function updates the orbit control configuration. The configuration object can have the following properties:
	 * @param {Object} orbitControlConfig - Configuration object for orbit controls.
	 * @param {Number} [orbitControlConfig.min_distance=3] - Minimum distance from the camera to the target
	 * @param {Number} [orbitControlConfig.max_distance=4] - Maximum distance from the camera to the target
	 */
	updateOrbitControlConfig(orbitControlConfig) {
		this.orbitControlConfig = orbitControlConfig;

		const { min_distance: minDistance, max_distance: maxDistance } =
			orbitControlConfig || {};

		if (this.controlMode === CONTROL_MODES.FIRST_PERSON) {
			// TODO: Adjust min/max Distance here
			const _minDistance = DEFAULT_FPS_MIN_DISTANCE_FROM_CAMERA;
			const _maxDistance = DEFAULT_FPS_MAX_DISTANCE_FROM_CAMERA;
			this.orbitControl.minDistance = _minDistance;
			this.orbitControl.maxDistance = _maxDistance;
		}
	}

	/**
	 * Updates camera target when the connected object is moved
	 * @private
	 */
	_updateCameraTarget = () => {
		if (!this.objectToTrackConnected) return;
		if (this.controlMode === CONTROL_MODES.FIRST_PERSON) {
			this.cameraTarget.x = this.objectToTrack.position.x;
			const _yOffset =
				this.yOffset !== null
					? this.yOffset
					: DEFAULT_FPS_CAMERA_Y_OFFSET;
			this.cameraTarget.y = this.objectToTrack.position.y + _yOffset;
			this.cameraTarget.z = this.objectToTrack.position.z;

			this.orbitControl.moveTo(
				this.cameraTarget.x,
				this.cameraTarget.y,
				this.cameraTarget.z,
			);

			// if (this.prevTarget && !this.avatar) {
			// 	const yDiff = this.cameraTarget.y - this.prevTarget.y;
			// 	if (Math.abs(yDiff) > 0.01) {
			// 		// positive yDiff when climbing stairs, negative when going down, rotate camera up and down accordingly
			// 		this.orbitControl.rotateUp(Math.sign(yDiff) * -0.01);
			// 	}
			// }
			// this.prevTarget = this.cameraTarget.clone();
		} else {
			this.playerCameraBoundingSphere.position.copy(
				this.playerCollider.position,
			);
			this.orbitControl.fitToSphere(
				this.playerCameraBoundingSphere,
				true,
			);
		}
	};

	// Listeners
	_handleVisibilityChange = () => {
		this.keysPressed = {};
		this.isWalking = false;
	};

	_rotateBasedOnJoystick = () => {
		let r = this.joyStickdata.vector.x;
		this.orbitControl.rotateLeft(-r / DEFAULT_JOYSTICK_ROTATION_SENSITIVITY);
	}

	_onStartMove = (evt) => {
		const { data } = evt.detail;
		if (!data.vector) return;
		this.isWalking = true;
		this.moveAngle = Math.atan2(data.vector.y, data.vector.x) - Math.PI / 2;
		this.joyStickFiring = true;
		this.joyStickdata = data;

		// this adds speed sensitivity to the joystick, moving the joystick farther means higher velocity
		this.setWalkVelocity(this.originalWalkVelocity * Math.sqrt(data.vector.x**2 + data.vector.y**2));
	};

	_onEndMove = () => {
		this.isWalking = false;
		this.moveAngle = null;
		this.joyStickFiring = false;
		this.setWalkVelocity(this.originalWalkVelocity);
	};

	_addKeyListeners() {
		// control keys
		this.keysPressed = {};
		document.addEventListener('keydown', this._handleKeydown, false);
		document.addEventListener('keyup', this._handleKeyup, false);
		window.addEventListener('joystickStartMove', this._onStartMove);
		window.addEventListener('joystickEndMove', this._onEndMove);
	}

	// State variables getters and setters
	get ready() {
		return this.#ready;
	}

	set moveAngle(value) {
		this.#moveAngle = value;
	}

	get moveAngle() {
		return this.#moveAngle;
	}

	set isWalking(value) {
		if (!this.ready) return;
		if (!this.objectToTrackConnected) return;
		this.#isWalking = value;
		if (value) {
			// Reduce PixelRatio while walking
			const suggestedPixelRatio = window.devicePixelRatio * 0.75;
			this.renderrer.setPixelRatio(suggestedPixelRatio);
			if (this.avatar) {
				this.avatar.setAnimation({
					name: ANIMATION_NAMES.walk,
					loop: THREE.LoopRepeat,
				});
			}
		} else {
			// Reset PixelRatio to default
			this.renderrer.setPixelRatio(window.devicePixelRatio);
			if (this.avatar) {
				this.avatar.setAnimation({
					name: ANIMATION_NAMES.idle,
					loop: THREE.LoopRepeat,
				});
			}
		}
	}

	get isWalking() {
		return this.#isWalking;
	}

	// Key Handlers
	_handleKeydown = (e) => {
		if (e.target?.localName !== 'body') {
			this._resetKeys();
			return;
		}
		const keyPressed = e.key.toLowerCase();

		if (DIRECTIONS.includes(keyPressed)) {
			this._onDirectionKeyAction(keyPressed, true);
		}
	};

	_handleKeyup = (e) => {
		if (e.target?.localName !== 'body') {
			this._resetKeys();
			return;
		}

		const keyReleased = e.key.toLowerCase();

		if (Object.keys(this.#animationKeysMap).includes(keyReleased)) {
			this._onAnimationKeyAction(keyReleased);
		}

		if (DIRECTIONS.includes(keyReleased)) {
			this._onDirectionKeyAction(keyReleased, false);
		}
	};

	_onAnimationKeyAction(key) {
		if (this.isWalking) return;

		this.avatar.setAnimation({
			name: this.#animationKeysMap[key],
			loop: THREE.LoopOnce,
			onEnd: () => {
				this.avatar.setAnimation({
					name: ANIMATION_NAMES.idle,
					loop: THREE.LoopRepeat,
				});
			},
		});
	}

	_onDirectionKeyAction(key, pressed) {
		if (pressed) {
			this.keysPressed[key] = true;
		} else {
			delete this.keysPressed[key];
		}

		if (this.rotateCameraTowardsMovementDirection) {
			this.isWalking = FORWARD_BACKWARD_KEYS.some(
				(key) => this.keysPressed[key] === true,
			);
		}

		else {
			this.isWalking = DIRECTIONS.some(
				(key) => this.keysPressed[key] === true,
			);
		}

		this.moveAngle = this._getAngleFromKeysPressed(this.keysPressed);
	}

	// Utility functions
	getCollidableMeshes = (storeObj) => {
		/** Returns a list of meshes to enable collisions on
		 *
		 * 1. If the model has a child named 'collision_mesh', only that mesh will be collidable
		 * 2. If the model has no 'collision_mesh', all meshes will be collidable
		 * **/
		storeObj.updateMatrixWorld();
		const collidableStoresMeshes = [];
		//check if the store contains 'collision_mesh'
		const collisionMesh = storeObj.getObjectByName(COLLIDABLE_MESH_NAME);
		if (collisionMesh) {
			collidableStoresMeshes.push(collisionMesh);
		} else {
			// all store is collidable
			storeObj.glbModel.traverseVisible((i) => {
				if (i.isMesh && !i.isSkinnedMesh) {
					collidableStoresMeshes.push(i);
				}
			});
		}
		return collidableStoresMeshes;
	};

	_moveGivenAngleChange(angleChange, delta) {
		if (!this.objectToTrackConnected) return;
		const angleYCameraDirection =
			Math.PI +
			Math.atan2(
				this.camera.position.x - this.objectToTrack.position.x,
				this.camera.position.z - this.objectToTrack.position.z,
			);

		const [moveX, moveZ] = this._getDeltaXDeltaYFromAngleChange(
			angleChange,
			angleYCameraDirection,
			delta,
		);

		this.objectToTrack.position.set(
			this.objectToTrack.position.x + moveX,
			this.objectToTrack.position.y,
			this.objectToTrack.position.z + moveZ,
		);

		this.camera.position.x += moveX;
		this.camera.position.z += moveZ;
		this.playerCollider.position.x += moveX;
		this.playerCollider.position.z += moveZ;
		this._checkBVH(delta);
		this._updateCameraTarget();
		// this.orbitControl.update();
	}

	// Collision Detection
	_checkBVH(delta) {
		if (!this.playerColliderReady) return;
		this.playerCollider.updateMatrixWorld();
		// adjust player position based on collisions
		this.colliderBoundingBox.makeEmpty();
		this.capsuleSegment.copy(this.playerCollider.capsuleInfo.segment);

		// get the position of the capsule in the local space of the collider
		this.capsuleSegment.start
			.applyMatrix4(this.playerCollider.matrixWorld)
			.applyMatrix4(this.invertedWorldMatrix);
		this.capsuleSegment.end
			.applyMatrix4(this.playerCollider.matrixWorld)
			.applyMatrix4(this.invertedWorldMatrix);

		// get the axis aligned bounding box of the capsule
		this.colliderBoundingBox.expandByPoint(this.capsuleSegment.start);
		this.colliderBoundingBox.expandByPoint(this.capsuleSegment.end);

		this.colliderBoundingBox.min.addScalar(
			-this.playerCollider.capsuleInfo.radius,
		);
		this.colliderBoundingBox.max.addScalar(
			this.playerCollider.capsuleInfo.radius,
		);

		this.storeCollider.geometry.boundsTree.shapecast({
			intersectsBounds: (box) =>
				box.intersectsBox(this.colliderBoundingBox),

			intersectsTriangle: (tri) => {
				// check if the triangle is intersecting the capsule and adjust the
				// capsule position if it is.
				const distance = tri.closestPointToSegment(
					this.capsuleSegment,
					this.triPoint,
					this.capsulePoint,
				);
				if (distance < this.playerCollider.capsuleInfo.radius) {
					const depth =
						this.playerCollider.capsuleInfo.radius - distance;
					const direction = this.capsulePoint
						.sub(this.triPoint)
						.normalize();

					this.capsuleSegment.start.addScaledVector(direction, depth);
					this.capsuleSegment.end.addScaledVector(direction, depth);
				}
			},
		});

		this.newColliderPosition
			.copy(this.capsuleSegment.start)
			.applyMatrix4(this.storeCollider.matrixWorld);

		// check how much the collider was moved
		this.colliderDeltaPosition.subVectors(
			this.newColliderPosition,
			this.playerCollider.position,
		);

		this.playerIsOnGround =
			this.colliderDeltaPosition.y >
			Math.abs(delta * this.playerVelocity.y * 0.25);

		this.offset = Math.max(0.0, this.colliderDeltaPosition.length() - 1e-5);
		this.colliderDeltaPosition.normalize().multiplyScalar(this.offset);

		// adjust the player model
		this.playerCollider.position.add(this.colliderDeltaPosition);
		this.objectToTrack.position.add(this.colliderDeltaPosition);
		this.camera.position.x += this.colliderDeltaPosition.x;
		this.camera.position.z += this.colliderDeltaPosition.z;

		if (!this.playerIsOnGround) {
			this.colliderDeltaPosition.normalize();
			this.playerVelocity.addScaledVector(
				this.colliderDeltaPosition,
				-this.colliderDeltaPosition.dot(this.playerVelocity),
			);
		} else {
			this.playerVelocity.set(0, 0, 0);
		}
	}

	_getDeltaXDeltaYFromAngleChange(
		angleChange,
		angleYCameraDirection,
		updateDelta,
	) {
		// rotate model
		this.rotateQuarternion.setFromAxisAngle(
			this.rotationAxis,
			angleChange + angleYCameraDirection,
		);
		this.objectToTrack.quaternion.rotateTowards(
			this.rotateQuarternion,
			0.1,
		);

		// calculate direction
		//walk direction is the same as the camera direction
		this.camera.getWorldDirection(this.walkDirection);
		this.walkDirection.applyAxisAngle(this.rotationAxis, angleChange);

		// move model & camera
		const moveX = this.walkDirection.x * this.walkVelocity * updateDelta;
		const moveZ = this.walkDirection.z * this.walkVelocity * updateDelta;

		return [moveX, moveZ];
	}

	_getAngleFromKeysPressed(keysPressed) {
		if (keysPressed['w'] || keysPressed['arrowup']) {
			if (this.rotateCameraTowardsMovementDirection)
				return 0; // Forward
			if (keysPressed['a'] || keysPressed['arrowleft']) {
				return Math.PI / 4; // w+a
			} else if (keysPressed['d'] || keysPressed['arrowright']) {
				return -Math.PI / 4; // w+d
			}
		} else if (keysPressed['s'] || keysPressed['arrowdown']) {
			if (this.rotateCameraTowardsMovementDirection)
				return Math.PI; // Backward
			if (keysPressed['a'] || keysPressed['arrowleft']) {
				return Math.PI / 4 + Math.PI / 2; // s+a
			} else if (keysPressed['d'] || keysPressed['arrowright']) {
				return -Math.PI / 4 - Math.PI / 2; // s+d
			} else {
				return Math.PI; // s
			}
		} else if (keysPressed['a'] || keysPressed['arrowleft']) {
			return Math.PI / 2; // a
		} else if (keysPressed['d'] || keysPressed['arrowright']) {
			return -Math.PI / 2; // d
		}
		return 0; // Default, no movement if only 'a' or 'd' is pressed
	}

	getCollidableChildrenArray() {
		return this.scene.children
			.filter((child) => {
				return (
					child.name === 'NavMarker' || child.name === 'HotspotMarker'
				);
			})
			.reduce((acc, curr) => {
				if (curr.children.length > 0) {
					acc.push(curr.getObjectByName('boxCollider'));
				}
				return acc;
			}, []);
	}

	_checkHotspotInteraction = () => {
		// Get the direction of the camera
		const cwd = new THREE.Vector3();
		this.camera.getWorldDirection(cwd);

		// Get the position of the camera
		const cwp = new THREE.Vector3();
		this.camera.getWorldPosition(cwp);

		// Set the raycaster to the camera position and direction
		this.interactionRaycaster.set(cwp, cwd);

		// Get the objects that intersect with the raycaster
		const intersects = this.interactionRaycaster.intersectObjects(
			this.getCollidableChildrenArray(),
			false,
		);

		if (intersects.length > 0) {
			intersects.sort((a, b) => a.distance - b.distance);
			if (
				this._activeHotspotId !== intersects?.[0]?.object?.uuid &&
				intersects?.[0]?.object?.parent?.onClick
			) {
				this._activeHotspotId = intersects?.[0]?.object?.uuid;
				intersects[0].object.parent.onClick();
			}
		}

		if (intersects.length === 0) {
			// Reset active hotspot id on moving away from the hotspot
			this._activeHotspotId = null;
		}
	};

	// Animation Loop
	update = (updateDelta) => {
		/**
		 * Update function for controls and avatar. This function updates the avatar's position and rotation based on user input.
		 * This function must be called on every frame you want the avatar to move.
		 *
		 * @param {number} updateDelta - The time in seconds since the last frame. This is used to ensure consistent movement speed regardless of framerate.
		 *
		 * **/

		const _clamped = Math.min(updateDelta, 0.02);
		// if the tab is not active, pause the animation loop
		if (document.visibilityState === 'hidden') return;

		if (!this.playerColliderReady || this.usingTempCollider) {
			this._setupPlayerCollider();
		}

		if (this.playerColliderReady) {
			// Ensures Gravity is applied
			this._checkBVH(_clamped);
			this.playerVelocity.y = this.playerIsOnGround
				? DEFAULT_GRAVITY
				: this.playerVelocity.y + DEFAULT_GRAVITY;

			if (this.playerVelocity.y < -1) {
				// Player is falling down from the air, we do not have a reset function for this as of now 8/31/23
				this._updateCameraTarget();
			}

			this.playerCollider.position.addScaledVector(
				this.playerVelocity,
				_clamped,
			);
			this.objectToTrack.position.copy(this.playerCollider.position);
			this.objectToTrack.position.y -= this.playerColliderRadius;
		}
		if (this.rotateCameraTowardsMovementDirection) {
			if (this.keysPressed['a'] || this.keysPressed['arrowleft']) {
				this.orbitControl.rotateLeft(DEFAULT_CAMERA_ROTATION_SPEED * _clamped);
			}
			if (this.keysPressed['d'] || this.keysPressed['arrowright']) {
				this.orbitControl.rotateLeft(-DEFAULT_CAMERA_ROTATION_SPEED * _clamped);
			}
		}

		// If player is walking, move the player
		if (this.isWalking) {
			this._moveGivenAngleChange(
				this.moveAngle,
				Math.min(_clamped, MAXIMUM_MULTIPLIER),
			);
		}

		// Update the avatar
		if (this.avatar) this.avatar.update(_clamped);

		this._checkHotspotInteraction();
		if (this.joyStickFiring && this.rotateCameraTowardsMovementDirection) this._rotateBasedOnJoystick();
		this.hotspotsFadeInOutEffect.tick(this.playerCollider);
	};

	tick = () => {
		this.update(Math.min(this.clock.getDelta(), 0.02));
	};

	addToAnimationLoop = () => {
		this.scene.addAnimationHook('rt3dControlsAnimationLoop', this.tick);
	};

	removeFromAnimationLoop = () => {
		this.scene.removeAnimationHook('rt3dControlsAnimationLoop');
	};

	// Cleanup
	_resetKeys = () => {
		this.keysPressed = {};
		this.isWalking = false;
	};

	dispose = () => {
		/**
		 * Disposes and resets the controls params. This function should be called when the controls are no longer needed.
		 * **/
		document.removeEventListener('keydown', this._handleKeydown);
		document.removeEventListener('keyup', this._handleKeyup);
		document.removeEventListener(
			'visibilitychange',
			this._handleVisibilityChange,
		);
		window.removeEventListener('joystickStartMove', this._onStartMove);
		window.removeEventListener('joystickEndMove', this._onEndMove);

		this.playerCollider = null;
		this.storeCollider = null;
		this.cameraCollider = null;

		this.removeFromAnimationLoop();
		this.keysPressed = {};
		this.orbitControl.moveTo(0.1, 0, 0, false);

		this.orbitControl.minDistance = 0;
		this.orbitControl.maxDistance = 0.1;
	};
}

export default RT3DControls;
